import { ReactElement, useCallback, useMemo, useState } from 'react';
import {
  DataGridPro,
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  GridFilterOperator,
  GridPaginationModel,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import useAuditLogsQuery from '@hooks/useAuditLogsQuery';
import { apiFields } from '@components/Admin/AuditLogs/auditLogUtils';
import dayjs, { Dayjs } from 'dayjs';
import storeConnector from '@store/storeConnector';
import { TimeMode } from '@types';
import { SiteMeta } from '@src/types/SiteMeta';
import AuditLogDialog from '@components/Admin/AuditLogs/AuditLogDialog';
import generateAuditLogColumns from '@components/Admin/AuditLogs/auditLogGridColumns';
import AuditLogsGridToolbar from '@components/Admin/AuditLogs/AuditLogsGridToolbar';
import { PaginationModel } from '@src/types/pagination/PaginationModel';
import EventTimeCell from '@components/Home/Alerts/Table/EventTimeCell';
import useAlias from '@alias';
import TimePeriodSelector from '@components/_shared/TimePeriodSelector/TimePeriodSelector';
import CustomRangeDialog from '@components/_shared/TimePeriodSelector/CustomRangeDialog';
import { useTimePeriodFilter } from '@hooks/useTimePeriodFilter';

interface AuditLogsProps {
  timeMode: TimeMode;
  siteMeta: SiteMeta;
}

const mapGridFilterToApiFilter = (
  filterItem: GridFilterItem,
): { key: keyof typeof apiFields; value: string } | null => {
  if (!filterItem.value) {
    return null;
  }

  switch (filterItem.field) {
    case apiFields.httpMethod:
      return { key: 'httpMethod', value: filterItem.value.toUpperCase() };
    case apiFields.username:
      return { key: 'username', value: filterItem.value };
    case apiFields.email:
      return { key: 'email', value: filterItem.value };
    case apiFields.url:
      return { key: 'url', value: filterItem.value };
    default:
      return null;
  }
};

function AuditLogs(props: AuditLogsProps): ReactElement {
  const alias = useAlias();
  const {
    selectedPeriod,
    timeRange,
    setPeriod,
    setCustomRange,
    isCustomRange,
  } = useTimePeriodFilter({
    initialPeriod: '24h',
    siteMeta: props.siteMeta,
    timeMode: props.timeMode,
    resetOnMount: true,
  });

  const [customRangeDialogOpen, setCustomRangeDialogOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [requestBodyDetails, setRequestBodyDetails] = useState<Record<
    string,
    string
  > | null>(null);
  const [additionalFilters, setAdditionalFilters] = useState<
    Partial<{
      username: string;
      email: string;
      url: string;
      httpMethod: string;
    }>
  >({});

  const apiBody = useMemo(
    () => ({
      from: timeRange.from,
      to: timeRange.to,
      page: paginationModel.page,
      count: paginationModel.pageSize,
      ...additionalFilters,
    }),
    [timeRange, paginationModel, additionalFilters],
  );

  const handleCustomRangeClick = (): void => {
    setCustomRangeDialogOpen(true);
  };

  const handleCustomRangeApply = (from: Dayjs, to: Dayjs): void => {
    setCustomRange(from, to);
    setCustomRangeDialogOpen(false);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleDetailsClick = (gridRow: GridValidRowModel) => (): void => {
    setRequestBodyDetails(gridRow.body);
  };

  const auditLogGridColumns: GridColDef[] = generateAuditLogColumns(
    handleDetailsClick,
    alias,
  );

  const betweenDateRangeFilterOperator: GridFilterOperator = {
    label: alias('Date Range'),
    value: 'between-date-range',
    headerLabel: alias('Date Range'),
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      const filterValue: boolean = filterItem.value;

      if (!filterItem.field || !filterValue || !filterItem.operator) {
        return null;
      }

      const [startRaw, endRaw] = filterItem.value.split(',');

      const start: Dayjs = dayjs(startRaw !== null ? startRaw : null);
      const end: Dayjs = dayjs(endRaw !== null ? endRaw : null);

      if (!start.isValid() || !end.isValid()) {
        return null;
      }

      return (rawValue) => {
        const value: Dayjs = dayjs(rawValue);
        return value.isValid() && value.isAfter(start) && value.isBefore(end);
      };
    },
  };

  const createdAtColumn: GridColDef = {
    headerName: alias('Created At'),
    description: alias('created at filter'),
    field: 'createdAt',
    minWidth: 200,
    headerAlign: 'center',
    renderCell: ({ value }) => <EventTimeCell time={new Date(value)} />,
    filterOperators: [betweenDateRangeFilterOperator],
  };

  auditLogGridColumns.push(createdAtColumn);

  const { rows, totalCount, isLoading, isFetching } =
    useAuditLogsQuery(apiBody);

  const onFilterChange = useCallback((filterModel: GridFilterModel): void => {
    const filters: GridFilterItem[] = filterModel.items;
    const filtersOn: boolean = filters.length > 0;

    if (
      filtersOn &&
      Object.prototype.hasOwnProperty.call(filters[0], 'value')
    ) {
      const newFilters: Partial<{
        username: string;
        email: string;
        url: string;
        httpMethod: string;
      }> = {};

      filterModel.items.forEach((filterItem: GridFilterItem): void => {
        const mapped = mapGridFilterToApiFilter(filterItem);
        if (mapped) {
          newFilters[mapped.key] = mapped.value;
        }
      });

      setAdditionalFilters(newFilters);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    } else {
      setAdditionalFilters({});
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  }, []);

  const paginationOnChange = (paginationEvent: GridPaginationModel): void => {
    setPaginationModel({
      page: paginationEvent.page,
      pageSize: paginationEvent.pageSize,
    });
  };

  const handleClose = (): void => {
    setRequestBodyDetails(null);
  };

  const CustomToolbar = useMemo(() => () => <AuditLogsGridToolbar />, []);

  return (
    <div className='home-row frow'>
      <div className='fcol h100 full-width'>
        <div className='cell block-container flex-1 overflow-y-auto'>
          <TimePeriodSelector
            selectedPeriod={isCustomRange ? 'custom' : selectedPeriod}
            onPeriodChange={setPeriod}
            showCustomRange
            onCustomRangeClick={handleCustomRangeClick}
          />

          <DataGridPro
            density='compact'
            pageSizeOptions={[25, 50, 75, 100]}
            pagination
            rowCount={totalCount}
            paginationMode='server'
            columns={auditLogGridColumns}
            rows={rows}
            paginationModel={paginationModel}
            onPaginationModelChange={paginationOnChange}
            filterMode='server'
            onFilterModelChange={onFilterChange}
            loading={isLoading || isFetching}
            slots={{ toolbar: CustomToolbar }}
            localeText={{
              toolbarColumns: alias('Columns'),
              toolbarFilters: alias('Filters'),
              toolbarExport: alias('Export'),
              toolbarQuickFilterPlaceholder: alias('Search...'),
            }}
          />

          <CustomRangeDialog
            open={customRangeDialogOpen}
            onClose={() => setCustomRangeDialogOpen(false)}
            onApply={handleCustomRangeApply}
            initialFrom={dayjs(timeRange.from)}
            initialTo={dayjs(timeRange.to)}
          />

          {requestBodyDetails && (
            <AuditLogDialog
              handleClose={handleClose}
              requestBodyDetails={requestBodyDetails}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default storeConnector(AuditLogs, {
  service: ['timeMode'],
  config: ['siteMeta'],
});
